import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDrawingStore } from '../../../stores/drawingStore';
import { ColorPicker } from '../../UI/ColorPicker';
import { Slider } from '../../UI/Slider';
import { isDragHandle } from '../../../interactions/drag';

interface SecondaryToolbarProps {
  className?: string;
  onClose: () => void;
  mainToolbarPosition?: { x: number; y: number };
}

export const SecondaryToolbar: React.FC<SecondaryToolbarProps> = ({ 
  className = '', 
  onClose, 
  mainToolbarPosition 
}) => {
  const {
    strokeWidth,
    strokeColor,
    fillColor,
    fillOpacity,
    setStrokeWidth,
    setStrokeColor,
    setFillColor,
    setFillOpacity,
  } = useDrawingStore();

  // Position the secondary toolbar next to the main toolbar
  const [position, setPosition] = useState(() => {
    if (mainToolbarPosition) {
      return {
        x: mainToolbarPosition.x + 80, // Offset to the right of main toolbar
        y: mainToolbarPosition.y
      };
    }
    return { x: 100, y: 20 };
  });
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isInDefaultPosition, setIsInDefaultPosition] = useState(true);
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Calculate default position
  const getDefaultPosition = useCallback(() => {
    if (mainToolbarPosition) {
      return {
        x: mainToolbarPosition.x + 80,
        y: mainToolbarPosition.y
      };
    }
    return { x: 100, y: 20 };
  }, [mainToolbarPosition]);

  // Check if current position is close to default position
  const checkIfInDefaultPosition = useCallback((currentPos: { x: number; y: number }) => {
    const defaultPos = getDefaultPosition();
    const tolerance = 5; // 5px tolerance
    return Math.abs(currentPos.x - defaultPos.x) < tolerance && 
           Math.abs(currentPos.y - defaultPos.y) < tolerance;
  }, [getDefaultPosition]);

  // Update position when main toolbar position changes (only if in default position)
  useEffect(() => {
    if (mainToolbarPosition && !isDragging && isInDefaultPosition) {
      const newPosition = getDefaultPosition();
      setPosition(newPosition);
    }
  }, [mainToolbarPosition, isDragging, isInDefaultPosition, getDefaultPosition]);

  // Handle mouse down for dragging
  const handleMouseDown = (event: React.MouseEvent) => {
    // Check if clicking on the drag handler area
    if (isDragHandle(event.target)) {
      setIsDragging(true);
      const rect = toolbarRef.current?.getBoundingClientRect();
      if (!rect) return;
      setDragOffset({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
    }
  };

  // Handle double click on drag handler to reset to default position
  const handleDoubleClick = (event: React.MouseEvent) => {
    if (isDragHandle(event.target)) {
      const defaultPos = getDefaultPosition();
      setPosition(defaultPos);
      setIsInDefaultPosition(true);
    }
  };

  // Handle mouse move for dragging
  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isDragging) return;

    const newX = event.clientX - dragOffset.x;
    const newY = event.clientY - dragOffset.y;

    // Keep toolbar within viewport bounds
    const maxX = window.innerWidth - (toolbarRef.current?.offsetWidth || 0);
    const maxY = window.innerHeight - (toolbarRef.current?.offsetHeight || 0);

    const newPosition = {
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    };

    setPosition(newPosition);
  }, [isDragging, dragOffset]);

  // Handle mouse up for dragging
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      // Check if the new position is close to default
      setIsInDefaultPosition(checkIfInDefaultPosition(position));
    }
  }, [isDragging, position, checkIfInDefaultPosition]);

  // Setup global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, position, handleMouseMove, handleMouseUp]);

  return (
    <div
      ref={toolbarRef}
      className={`fixed z-40 bg-white/95 backdrop-blur-md border border-gray-200 rounded-xl shadow-xl p-4 ${className}`}
      style={{
        left: position.x,
        top: position.y,
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
    >
      {/* Header with drag handler and close button */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          {/* Drag Handler - 6 dots in 2 rows with larger draggable area */}
          <div className="drag-handler flex items-center cursor-move hover:cursor-move group">
            <div className="flex flex-col space-y-1">
              {/* Top row - 3 dots */}
              <div className="flex space-x-1">
                <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors"></div>
                <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors"></div>
                <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors"></div>
              </div>
              {/* Bottom row - 3 dots */}
              <div className="flex space-x-1">
                <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors"></div>
                <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors"></div>
                <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-gray-600 transition-colors"></div>
              </div>
            </div>
          </div>
          
          <h3 className="text-sm font-semibold text-gray-700">Tool Settings</h3>
        </div>
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
        >
          ×
        </button>
      </div>
      
      <div className="space-y-4">
        {/* Stroke Width */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-600">Stroke Width</label>
          <Slider
            value={strokeWidth}
            onChange={setStrokeWidth}
            min={1}
            max={20}
            step={1}
          />
          <div className="text-xs text-gray-500 font-mono">{strokeWidth}px</div>
        </div>

        {/* Stroke Color */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-600">Stroke Color</label>
          <ColorPicker
            color={strokeColor}
            onChange={setStrokeColor}
          />
        </div>

        {/* Fill Color */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-600">Fill Color</label>
          <ColorPicker
            color={fillColor}
            onChange={setFillColor}
          />
        </div>

        {/* Fill Opacity */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-600">Fill Opacity</label>
          <Slider
            value={fillOpacity}
            onChange={setFillOpacity}
            min={0}
            max={1}
            step={0.1}
          />
          <div className="text-xs text-gray-500 font-mono">{Math.round(fillOpacity * 100)}%</div>
        </div>

        {/* Layer Controls */}
        <div className="pt-3 border-t border-gray-200">
          <h4 className="text-xs font-medium text-gray-600 mb-3">Layers</h4>
          <div className="grid grid-cols-2 gap-2">
            <button className="px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300 transition-colors duration-200 hover:shadow-sm">
              ↓↓ Back
            </button>
            <button className="px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300 transition-colors duration-200 hover:shadow-sm">
              ↓ Back
            </button>
            <button className="px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300 transition-colors duration-200 hover:shadow-sm">
              ↑ Forward
            </button>
            <button className="px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300 transition-colors duration-200 hover:shadow-sm">
              ↑↑ Front
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
